import React from "react";
import type {
  ClinicalProgressionMetrics as Metrics,
  WorkingDrgSummary,
} from "./types";

type Props = {
  metrics?: Metrics | null;
  workingDrg?: WorkingDrgSummary | null;
};

const formatDrift = (value: number): string => {
  if (value === 0) return "0";
  return value > 0 ? `+${value}` : `${value}`;
};

const ClinicalProgressionMetrics: React.FC<Props> = ({
  metrics,
  workingDrg,
}) => {
  if (!metrics && !workingDrg) {
    return null;
  }

  const drift =
    metrics &&
    (typeof metrics.losDrift === "number"
      ? metrics.losDrift
      : metrics.currentLos - metrics.gmlos);
  const driftPositive = typeof drift === "number" && drift > 0;

  return (
    <div className="flex flex-nowrap items-center gap-2 overflow-x-auto border-b border-gray-200 pb-2">
      {metrics ? (
        <>
          <div className="flex shrink-0 items-center gap-1.5 rounded-md border border-[#C5CBE8] bg-[#E8ECF8] px-2.5 py-1 leading-none">
            <span className="text-xs font-medium text-[#6B728E]">GMLOS</span>
            <span className="text-xs font-bold tabular-nums text-[#1E293B]">
              {metrics.gmlos}
            </span>
          </div>

          <div className="flex shrink-0 items-center gap-1.5 rounded-md border border-[#D4C8E8] bg-[#F0EBF7] px-2.5 py-1 leading-none">
            <span className="text-xs font-medium text-[#7A6B8E]">
              Current LOS
            </span>
            <span className="text-xs font-bold tabular-nums text-[#1E293B]">
              {metrics.currentLos}
            </span>
          </div>

          <div
            className={`flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1 leading-none ${
              driftPositive
                ? "border-[#F87171] bg-[#FECACA]"
                : "border-[#4ADE80] bg-[#E8F8EE]"
            }`}
          >
            <span
              className={`text-xs font-medium ${
                driftPositive ? "text-[#991B1B]" : "text-[#166534]"
              }`}
            >
              LOS Drift
            </span>
            <span
              className={`text-xs font-bold tabular-nums ${
                driftPositive ? "text-[#991B1B]" : "text-[#166534]"
              }`}
            >
              {formatDrift(drift as number)}
            </span>
          </div>
        </>
      ) : null}

      {workingDrg ? (
        <>
          <div className="flex min-w-0 shrink items-center gap-1.5 rounded-md border border-gray-200 bg-[#E8F1FB] px-2.5 py-1 leading-none">
            <span className="shrink-0 text-xs font-medium text-slate-600">
              Working DRG
            </span>
            <span className="min-w-0 truncate text-xs font-bold text-[#1E293B]">
              {workingDrg.workingDrgCode} - {workingDrg.workingDrgDescription}
            </span>
            {workingDrg.workingDrgCategory ? (
              <span className="shrink-0 rounded-full border border-[#93C5FD] bg-[#DBEAFE] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#1D4ED8]">
                {workingDrg.workingDrgCategory}
              </span>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-1.5 rounded-md border border-gray-200 bg-[#E8F1FB] px-2.5 py-1 leading-none">
            <span className="text-xs font-medium text-slate-600">
              DRG Weight
            </span>
            <span className="text-xs font-bold tabular-nums text-[#1E293B]">
              {Number.isFinite(workingDrg.drgWeight)
                ? workingDrg.drgWeight
                : "--"}
            </span>
          </div>

          <div
            className="flex min-w-0 shrink items-center gap-1.5 rounded-md border border-transparent px-2.5 py-1 leading-none"
            style={{ backgroundColor: "oklch(90.3% .076 319.62)" }}
          >
            <span className="shrink-0 text-xs font-medium text-slate-600">
              Primary Diagnosis
            </span>
            <span className="shrink-0 text-xs font-bold tabular-nums text-[#1E293B]">
              {workingDrg.primaryDiagnosisCode}
            </span>
            <span className="min-w-0 truncate text-xs font-bold text-[#1E293B]">
              {workingDrg.primaryDiagnosisDescription}
            </span>
            {(workingDrg.primaryDiagnosisTags ?? []).map((tag) => (
              <span
                key={tag}
                className="shrink-0 rounded-full bg-emerald-600 px-1.5 py-0.5 text-[10px] font-bold uppercase text-white"
              >
                {tag}
              </span>
            ))}
            <span className="shrink-0 rounded-full bg-[#1d4ed8] px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
              PDx
            </span>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default ClinicalProgressionMetrics;
